import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Pencil, Check, RotateCcw, Unlink, Wrench, Info } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { ConnectionIndicators, getPowerStatus, getLocale, deviceStatusLabel } from './DashboardPage';
import {
  useShelterService, ShelterItems, ServiceRecord, ServiceHistoryList,
} from '../components/service/ShelterServicePanel';

// ─── Perustiedot + Laitetieto — PoC 6 (2.9.2026) ──────────────────────────────
// Superadmin muokkaa, Admin tarkastelee. Osoite ei ole muokattavissa täältä —
// se pysyy koontinäkymän inline-muokkauksen kautta (PoC 5). Nimi siirrettiin
// tänne 19.9.2026 (PoC 7): suojatilan luonti ei enää kysy nimeä etukäteen,
// joten Super-Adminin on voitava antaa se tällä kortilla.

function Field({ label, value }) {
  return (
    <div>
      <dt className="text-xs text-gray-400">{label}</dt>
      <dd className="text-sm text-gray-800 mt-0.5">{value}</dd>
    </div>
  );
}

function EditField({ label, type = 'text', value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full rounded-lg border border-gray-300 text-sm px-3 py-2 focus:outline-none focus:border-brand-500"
      />
    </div>
  );
}

// ─── Huoltotila (#44, 1.10.2026) ──────────────────────────────────────────────
// Superadmin aloittaa huollon esim. laitteen RutOS-päivityksen ajaksi: hälytykset
// ovat hiljaa, huolto päättyy automaattisesti, ja päättyessä aloittaja ja support
// saavat raportin laitteen tilasta (backend/src/services/maintenance.js).
// Admin näkee käynnissä olevan huollon, mutta ei aloita eikä lopeta sitä.

const MAINTENANCE_HOURS = [1, 2, 4, 8, 24];
// Jatko (#80): koko huolto enintään 24 h aloituksesta (backend/src/services/maintenance.js).
const EXTEND_HOURS = [1, 2, 4, 8];
const MAX_MAINTENANCE_HOURS = 24;

function MaintenancePanel({ shelter, isSuperadmin, onChanged }) {
  const { t, i18n } = useTranslation();
  const [hours, setHours] = useState(1);
  const [extendHours, setExtendHours] = useState(1);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const m = shelter.maintenance;

  if (!m && !isSuperadmin && !result) return null;

  const fmt = (ts) => new Date(ts).toLocaleString(getLocale(i18n.language), {
    day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  async function start() {
    setBusy(true); setError(''); setResult(null);
    try {
      await api.post(`/api/shelters/${shelter.id}/maintenance`, { hours, notes: notes.trim() || undefined });
      setNotes('');
      await onChanged();
    } catch (err) {
      setError(err.response?.data?.error || t('maintenanceMode.failed'));
    } finally {
      setBusy(false);
    }
  }

  const extendOptions = m
    ? EXTEND_HOURS.filter(h => Number(m.estimated_hours) + h <= MAX_MAINTENANCE_HOURS)
    : [];

  async function extend() {
    setBusy(true); setError('');
    try {
      const h = extendOptions.includes(extendHours) ? extendHours : extendOptions[0];
      await api.post(`/api/shelters/${shelter.id}/maintenance/extend`, { hours: h });
      await onChanged();
    } catch (err) {
      setError(err.response?.data?.error || t('maintenanceMode.failed'));
    } finally {
      setBusy(false);
    }
  }

  async function end() {
    setBusy(true); setError('');
    try {
      const { data } = await api.post(`/api/shelters/${shelter.id}/maintenance/end`);
      setResult(data);
      await onChanged();
    } catch (err) {
      setError(err.response?.data?.error || t('maintenanceMode.failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`rounded-lg border p-3 ${m ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'}`}>
      <h3 className="text-sm font-medium text-gray-800 mb-2">{t('maintenanceMode.title')}</h3>

      {m ? (
        <div className="space-y-2 text-sm">
          <p className="font-medium text-amber-900">{t('maintenanceMode.active', { until: fmt(m.ends_at) })}</p>
          <p className="text-gray-600">
            {t('maintenanceMode.startedBy')}: {m.started_by_email || '—'} · {fmt(m.started_at)}
            {m.notes ? <> · {m.notes}</> : null}
          </p>
          {isSuperadmin && (
            <div className="flex flex-wrap items-center gap-2 mt-1">
              {extendOptions.length > 0 ? (
                <>
                  <select
                    value={extendOptions.includes(extendHours) ? extendHours : extendOptions[0]}
                    onChange={e => setExtendHours(parseInt(e.target.value, 10))}
                    disabled={busy}
                    aria-label={t('maintenanceMode.extendBy')}
                    className="rounded-lg border border-amber-300 bg-white text-sm px-2 py-1.5 focus:outline-none focus:border-amber-500"
                  >
                    {extendOptions.map(h => <option key={h} value={h}>+{t('maintenanceMode.hours', { count: h })}</option>)}
                  </select>
                  <button
                    onClick={extend}
                    disabled={busy}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium border border-amber-600 text-amber-800 bg-white hover:bg-amber-100 disabled:opacity-50"
                  >
                    {busy ? t('maintenanceMode.extending') : t('maintenanceMode.extend')}
                  </button>
                </>
              ) : (
                <span className="text-xs text-gray-600">{t('maintenanceMode.extendMax')}</span>
              )}
              <button
                onClick={end}
                disabled={busy}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {busy ? t('maintenanceMode.ending') : t('maintenanceMode.end')}
              </button>
            </div>
          )}
        </div>
      ) : isSuperadmin ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">{t('maintenanceMode.help')}</p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('maintenanceMode.duration')}</label>
              <select
                value={hours}
                onChange={e => setHours(parseInt(e.target.value, 10))}
                className="rounded-lg border border-gray-300 text-sm px-3 py-2 focus:outline-none focus:border-brand-500"
              >
                {MAINTENANCE_HOURS.map(h => <option key={h} value={h}>{t('maintenanceMode.hours', { count: h })}</option>)}
              </select>
            </div>
            <div className="flex-1 min-w-[12rem]">
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('maintenanceMode.notes')}</label>
              <input
                type="text"
                value={notes}
                maxLength={500}
                onChange={e => setNotes(e.target.value)}
                placeholder={t('maintenanceMode.notesPlaceholder')}
                className="w-full rounded-lg border border-gray-300 text-sm px-3 py-2 focus:outline-none focus:border-brand-500"
              />
            </div>
            <button
              onClick={start}
              disabled={busy}
              className="px-3 py-2 rounded-lg text-sm font-medium bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {busy ? t('maintenanceMode.starting') : t('maintenanceMode.start')}
            </button>
          </div>
        </div>
      ) : null}

      {result && (
        <div className={`mt-3 text-sm rounded-lg p-3 ${result.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          <p className="font-medium">{result.ok ? t('maintenanceMode.resultOk') : t('maintenanceMode.resultProblems')}</p>
          {!result.ok && <ul className="list-disc ml-5 mt-1">{result.problems.map(p => <li key={p}>{p}</li>)}</ul>}
          {result.changes?.length > 0 && <p className="mt-1 text-gray-600">{result.changes.join(' · ')}</p>}
          <p className="mt-1 text-gray-500">{t('maintenanceMode.reportSent')}</p>
        </div>
      )}
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
    </div>
  );
}

// ─── Vuosihuolto (#82 versio 9, 29.10.2026) ───────────────────────────────────
// SafePron huoltoasiantuntija (Superadmin) päivittää vuosittaisen huollon päivän Huolto-
// kentässä. Päivä näkyy kaikille rooleille perustiedoissa (korvasi "Viimeisin huolto").

function AnnualServiceEditor({ shelter, onSaved }) {
  const { t } = useTranslation();
  const current = shelter.annual_service_date ? String(shelter.annual_service_date).slice(0, 10) : '';
  const [value, setValue] = useState(current);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const today = new Date().toISOString().slice(0, 10);

  async function save() {
    setSaving(true); setError(''); setSaved(false);
    try {
      const { data } = await api.put(`/api/shelters/${shelter.id}/annual-service`, { date: value || null });
      setSaved(true);
      onSaved(data.annual_service_date);
    } catch (err) {
      setError(err.response?.data?.error || t('common.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 p-3">
      <h3 className="text-sm font-medium text-gray-800 mb-1">{t('shelterCard.annualService')}</h3>
      <p className="text-xs text-gray-500 mb-2">{t('shelterCard.annualServiceHelp')}</p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="block text-xs font-medium text-gray-600 mb-1">{t('shelterCard.annualServiceDate')}</span>
          <input type="date" value={value} max={today} onChange={e => { setValue(e.target.value); setSaved(false); }}
            className="rounded-lg border border-gray-300 text-sm px-3 py-2 focus:outline-none focus:border-brand-500" />
        </label>
        <button
          type="button"
          onClick={save}
          disabled={saving || value === current}
          className="flex items-center gap-1.5 bg-brand-700 hover:bg-brand-900 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed"
        >
          <Check size={13} /> {saving ? t('common.saving') : t('shelterCard.saveAnnualService')}
        </button>
        {saved && value === current && <span className="text-sm text-green-700 pb-2">{t('shelterCard.notesSaved')}</span>}
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
}

// ─── Muistiinpano (#82 versio 6, 29.10.2026) ──────────────────────────────────
// Vapaa teksti suojatilasta. Superadmin ja oman asiakkaan Admin näkevät ja muokkaavat.

function NotesEditor({ shelter, onSaved }) {
  const { t } = useTranslation();
  const [value, setValue] = useState(shelter.notes || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const changed = value.trim() !== (shelter.notes || '');

  async function save() {
    setSaving(true); setError(''); setSaved(false);
    try {
      const { data } = await api.put(`/api/shelters/${shelter.id}/notes`, { notes: value });
      setValue(data.notes || '');
      setSaved(true);
      onSaved(data.notes);
    } catch (err) {
      setError(err.response?.data?.error || t('common.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <label htmlFor="shelter-notes" className="block text-sm font-medium text-gray-800 mb-2">{t('shelterCard.notes')}</label>
      <textarea
        id="shelter-notes"
        value={value}
        onChange={e => { setValue(e.target.value); setSaved(false); }}
        maxLength={4000}
        rows={4}
        placeholder={t('shelterCard.notesPlaceholder')}
        className="w-full rounded-lg border border-gray-300 text-sm px-3 py-2 focus:outline-none focus:border-brand-500"
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      <div className="flex items-center gap-3 mt-2">
        <button
          type="button"
          onClick={save}
          disabled={saving || !changed}
          className="flex items-center gap-1.5 bg-brand-700 hover:bg-brand-900 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed"
        >
          <Check size={13} /> {saving ? t('common.saving') : t('shelterCard.saveNotes')}
        </button>
        {saved && !changed && <span className="text-sm text-green-700">{t('shelterCard.notesSaved')}</span>}
      </div>
    </div>
  );
}

// ─── Suojatilakortti (#82 versiot 6 ja 8, 29.10.2026) ──────────────────────────
// Kaksi kenttää: Suojatilan tiedot (tila, perustiedot ja laitetieto, tarvikkeet ja
// muistiinpano) ja Huolto (huoltotila, vuosihuolto, huoltokirjaus ja historia). Huolto-kenttä on vain
// Superadminille. Admin näkee tarvikkeet vain luettavina ja muokkaa muistiinpanoa.
// Käynnissä olevan huoltotilan Admin näkee koontinäytön merkistä.

export default function ShelterCardPage() {
  const { id } = useParams();
  const { profile } = useAuth();
  const { t, i18n } = useTranslation();
  const isSuperadmin = profile?.role === 'superadmin';
  // Huolto-rooli (#86): huoltotoimet ja perustiedot (myös sijainti), ei laitetta eikä Safe Digiä.
  const isHuolto = profile?.role === 'huolto';
  const canService = isSuperadmin || isHuolto;
  const fromService = useLocation().state?.from === 'huolto';

  const [shelter, setShelter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [unlinking, setUnlinking] = useState(false);

  // silent: päivitys ilman lataustilaa, jotta kortin osat (esim. huoltopaneelin
  // tulos) eivät poistu näkyvistä ja menetä tilaansa.
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setLoadError('');
    try {
      const { data } = await api.get(`/api/shelters/${id}`);
      setShelter(data);
    } catch (err) {
      setLoadError(err.response?.data?.error || t('shelterCard.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => { load(); }, [load]);

  const service = useShelterService(id, true);

  function startEdit() {
    setForm({
      name: shelter.name ?? '',
      location: shelter.location ?? '',
      person_count: shelter.person_count ?? '',
      delivery_year: shelter.delivery_year ?? '',
      inactive: !!shelter.inactive,
      teltonika_model: shelter.teltonika_model ?? '',
      serial_number: shelter.serial_number ?? '',
      shelter_model: shelter.shelter_model ?? '',
      safe_digi: shelter.safe_digi !== false,
    });
    setSaveError('');
    setEditing(true);
  }

  async function save() {
    setSaving(true);
    setSaveError('');
    try {
      await api.patch(`/api/shelters/${id}`, {
        name: form.name?.trim() || shelter.name,
        location: isHuolto ? (form.location.trim() || null) : shelter.location,
        person_count: form.person_count === '' ? null : parseInt(form.person_count, 10),
        delivery_year: form.delivery_year === '' ? null : parseInt(form.delivery_year, 10),
        inactive: form.inactive,
        serial_number: form.serial_number,
        shelter_model: form.shelter_model,
        ...(isSuperadmin ? { teltonika_model: form.teltonika_model, safe_digi: form.safe_digi } : {}),
      });
      setEditing(false);
      await load();
    } catch (err) {
      setSaveError(err.response?.data?.error || t('common.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  // PoC 7 (15.9.2026) — laitteen irrotus siirretty tänne entisestä Laiterekisteri-
  // välilehdestä (TenantsPage.js, poistettu). Irrotus palauttaa laitteen
  // pending-tilaan ja mitätöi sen MQTT-tunnukset (backend/src/routes/devices.js).
  async function unlinkDevice() {
    if (!window.confirm(t('shelterCard.unlinkConfirm', { imei: shelter.imei }))) return;
    setUnlinking(true);
    try {
      await api.post(`/api/devices/${shelter.imei}/unlink`);
      await load();
    } finally {
      setUnlinking(false);
    }
  }

  if (loading) return <div className="text-center py-16 text-gray-400 text-sm">{t('common.loading')}</div>;
  if (loadError) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-sm text-red-700 max-w-3xl">
      {loadError}
    </div>
  );
  if (!shelter) return null;

  const powerStatus = getPowerStatus(shelter);
  // Vain huollettava suojatila (#82 versio 10): ei laitetta eikä valvontaa, vain Superadmin näkee.
  const safeDigi = shelter.safe_digi !== false;
  const h2 = 'text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5';

  return (
    <div className="max-w-3xl">
      <Link to={fromService ? '/huolto' : '/dashboard'} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-4">
        <ArrowLeft size={14} /> {fromService ? t('service.backToService') : t('common.backToDashboard')}
      </Link>

      <div className="flex items-center justify-between mb-1">
        <h1 className="text-xl font-bold text-gray-900">{shelter.name}</h1>
        <button onClick={() => { load(); service.reload(); }} className="text-gray-400 hover:text-gray-700" title={t('common.refresh')}>
          <RotateCcw size={15} />
        </button>
      </div>
      {shelter.location && <p className="text-sm text-gray-500 mb-5">{shelter.location}</p>}

      {/* ── Suojatilan tiedot ── */}
      <section className="bg-white rounded-xl border border-gray-200 p-4 divide-y divide-gray-100">
        <div className="pb-4">
          <h2 className={`${h2} mb-3`}><Info size={13} /> {t('shelterCard.infoTitle')}</h2>
          {!safeDigi && <p className="text-sm text-gray-600">{t('shelterCard.serviceOnlyNote')}</p>}
          {/* Elävä tila */}
          {safeDigi && <div className="flex flex-wrap items-center gap-6">
            <div>
              <p className="text-xs text-gray-400 mb-1">{t('dashboard.list.device', 'Laite')}</p>
              <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${shelter.online ? 'text-green-600' : 'text-red-600'}`}>
                <span className={`w-2 h-2 rounded-full ${shelter.online ? 'bg-green-400' : 'bg-red-400'}`} />
                {deviceStatusLabel(shelter, t)}
              </span>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-1">{t('sensors.power', 'Verkkovirta')}</p>
              {powerStatus === 'ok' && <span className="text-sm font-medium text-green-600">{t('sensors.powerOk', 'OK')}</span>}
              {powerStatus === 'fail' && <span className="text-sm font-medium text-red-600">{t('sensors.powerFail', 'Poikki')}</span>}
              {powerStatus === 'untested' && <span className="text-sm font-medium text-amber-600">{t('sensors.powerUntested', 'Testaamatta')}</span>}
              {powerStatus === 'unknown' && <span className="text-sm text-gray-400">{t('sensors.powerUnknown', 'Ei tietoa')}</span>}
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-1">{t('dashboard.list.connections', 'Yhteydet')}</p>
              <ConnectionIndicators shelter={shelter} t={t} />
            </div>
          </div>}
          {/* #7: vihje vianetsintään vain Superadminille (tunnusten haku = HTTPS 443 toimii) */}
          {isSuperadmin && shelter.never_connected && (
            <p className="mt-3 text-xs text-red-600">
              {shelter.provisioned_at
                ? t('shelterCard.neverConnectedCreds', { time: new Date(shelter.provisioned_at).toLocaleString(getLocale(i18n.language)) })
                : t('shelterCard.neverConnectedNoCreds', { time: new Date(shelter.device_linked_at).toLocaleString(getLocale(i18n.language)) })}
            </p>
          )}
        </div>

        {/* Perustiedot + Laitetieto */}
        <div className="py-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-800">{t('shelterCard.basicAndDeviceInfo')}</h3>
            {canService && !editing && (
              <div className="flex items-center gap-3">
                {isSuperadmin && shelter.imei && (
                  <button
                    onClick={unlinkDevice}
                    disabled={unlinking}
                    className="flex items-center gap-1 text-sm text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                    title={`IMEI ${shelter.imei}`}
                  >
                    <Unlink size={13} /> {unlinking ? t('shelterCard.unlinking') : t('shelterCard.unlinkDevice')}
                  </button>
                )}
                <button onClick={startEdit} className="flex items-center gap-1 text-sm text-brand-700 hover:text-brand-900 font-medium">
                  <Pencil size={13} /> {t('common.edit')}
                </button>
              </div>
            )}
          </div>

          {!editing ? (
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
              <Field label={t('shelter.name')} value={shelter.name ?? '—'} />
              <Field label={t('shelter.personCount')} value={shelter.person_count ?? '—'} />
              <Field label={t('shelter.deliveryYear')} value={shelter.delivery_year ?? '—'} />
              <Field label={t('shelterCard.annualService')} value={shelter.annual_service_date ? new Date(shelter.annual_service_date).toLocaleDateString(getLocale(i18n.language)) : '—'} />
              <Field label={t('shelter.inactive')} value={shelter.inactive ? t('common.yes') : t('common.no')} />
              <Field label={t('dashboard.list.shelterModel', 'Suojatila malli')} value={shelter.shelter_model ?? '—'} />
              <Field label={t('shelterCard.shelterSerialNumber')} value={shelter.serial_number ?? '—'} />
              {isSuperadmin && <Field label={t('shelterCard.safeDigi')} value={safeDigi ? t('common.yes') : t('common.no')} />}
              {safeDigi && <>
                <Field label={t('shelterCard.teltonikaModel')} value={shelter.teltonika_model ?? '—'} />
                <Field label={t('shelterCard.teltonikaSerialNumber')} value={shelter.teltonika_serial_number ?? '—'} />
                <Field label={t('shelterCard.firmware')} value={shelter.firmware_version ?? '—'} />
                <Field label={t('shelterCard.agentVersion')} value={shelter.agent_version ?? '—'} />
              </>}
            </dl>
          ) : (
            <form onSubmit={e => { e.preventDefault(); save(); }} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <EditField label={t('shelter.name')} value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} />
                {isHuolto && <EditField label={t('shelter.location')} value={form.location} onChange={v => setForm(f => ({ ...f, location: v }))} />}
                <EditField label={t('shelter.personCount')} type="number" value={form.person_count} onChange={v => setForm(f => ({ ...f, person_count: v }))} />
                <EditField label={t('shelter.deliveryYear')} type="number" value={form.delivery_year} onChange={v => setForm(f => ({ ...f, delivery_year: v }))} />
                <label className="flex items-center gap-2 text-xs font-medium text-gray-600 mt-6">
                  <input
                    type="checkbox"
                    checked={form.inactive}
                    onChange={e => setForm(f => ({ ...f, inactive: e.target.checked }))}
                  />
                  {t('shelter.inactive')}
                </label>
                <EditField label={t('dashboard.list.shelterModel', 'Suojatila malli')} value={form.shelter_model} onChange={v => setForm(f => ({ ...f, shelter_model: v }))} />
                <EditField label={t('shelterCard.shelterSerialNumber')} value={form.serial_number} onChange={v => setForm(f => ({ ...f, serial_number: v }))} />
                {isSuperadmin && form.safe_digi && <EditField label={t('shelterCard.teltonikaModel')} value={form.teltonika_model} onChange={v => setForm(f => ({ ...f, teltonika_model: v }))} />}
                {isSuperadmin && (
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-600 mt-6" title={shelter.imei ? t('shelterCard.safeDigiLockedByDevice') : undefined}>
                    <input
                      type="checkbox"
                      checked={form.safe_digi}
                      disabled={!!shelter.imei}
                      onChange={e => setForm(f => ({ ...f, safe_digi: e.target.checked }))}
                    />
                    {t('shelterCard.safeDigi')}
                  </label>
                )}
              </div>

              {saveError && <p className="text-sm text-red-600">{saveError}</p>}

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setEditing(false)} className="text-sm text-gray-500 hover:text-gray-800 px-3 py-2">
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-1.5 bg-brand-700 hover:bg-brand-900 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-60 transition-colors"
                >
                  {saving ? <RotateCcw size={13} className="animate-spin" /> : <Check size={13} />}
                  {t('common.save')}
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="py-4">
          <ShelterItems service={service} canEdit={canService} />
        </div>

        <div className="pt-4">
          <NotesEditor shelter={shelter} onSaved={notes => setShelter(s => ({ ...s, notes }))} />
        </div>
      </section>

      {/* ── Huolto ── (Superadmin ja Huolto-rooli, #86) */}
      {canService && (
        <section id="huolto" className="bg-white rounded-xl border border-gray-200 p-4 mt-6 space-y-4 scroll-mt-4">
          <h2 className={h2}><Wrench size={13} /> {t('shelterCard.serviceTitle')}</h2>
          {safeDigi && <MaintenancePanel shelter={shelter} isSuperadmin onChanged={() => load(true)} />}
          <AnnualServiceEditor shelter={shelter} onSaved={d => setShelter(s => ({ ...s, annual_service_date: d }))} />
          <div className="rounded-lg border border-gray-200 p-3">
            <h3 className="text-sm font-medium text-gray-800 mb-2">{t('service.recordSectionTitle')}</h3>
            <ServiceRecord service={service} canEdit onSaved={() => load(true)} />
          </div>
          <ServiceHistoryList service={service} />
        </section>
      )}
    </div>
  );
}
